import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { NumberUtils } from '../utils/number-utils';
import {
    ApiRequest,
    MarketValueData
} from '../components/sb-market-value-over-time-chart-component/sb-market-value-over-time-chart.model';
import { HttpClient } from '@angular/common/http';
import { formatDate } from '@angular/common';
import { AppConfigQuery } from '../../core/state/app-config/app-config.query';

@Injectable({
    providedIn: 'root'
})
export class SbMarketValueOverTimeChartService {
    baseApiUrl = `${
        this.config.get().consumerBaseUrl
    }/v1/consume_portfolio_data/market_value_over_time/request`;

    constructor(private config: AppConfigQuery, private http: HttpClient) {}

    /**
     * Gets the last value in the given data.
     * Data must be sorted by date.
     * @param data
     */
    static getCurrentTotalMarketValue(data: MarketValueData): number {
        const lastItem = data[0].data[data[0].data.length - 1];
        return lastItem ? lastItem[1] : 0;
    }

    /**
     * Gets the difference between the first and last values in the given data.
     * Data must be sorted by date.
     * @param data
     */
    static getAbsoluteChange(data: MarketValueData): number {
        const lastItem = data[0].data[data[0].data.length - 1];
        const firstItem = data[0].data[0];
        if (!lastItem || !firstItem) {
            return 0;
        }
        return lastItem[1] - firstItem[1];
    }

    /**
     * Makes http post request for Market Value Data.
     * @param request
     */
    postMarketValueData(request: ApiRequest): Observable<MarketValueData> {
        return this.http.post<MarketValueData>(this.baseApiUrl, request).pipe(
            catchError((error) => {
                console.error(
                    'There was an error while getting Market Value Data.',
                    error
                );
                return of({ name: '', data: [] });
            })
        );
    }

    /**
     * Formats absolue change into a proper string.
     * @param rawAbsoluteChange
     */
    static refineAbsoluteChangeLabel(rawAbsoluteChange: number): string {
        let absoluteChange = NumberUtils.formatNumberToSeperated(rawAbsoluteChange);
        if (rawAbsoluteChange > 0) {
            absoluteChange = '+' + absoluteChange;
        }
        return absoluteChange;
    }
}

export const MARKET_VALUE_OVER_TIME_CHART_OPTIONS = {
    data: {
        types: {
            'Market Value': 'area-spline'
        }
    },
    legend: {
        show: false
    },
    line: {
        connectNull: true
    },
    axis: {
        x: {
            type: 'timeseries'
        },
        y: {
            tick: {
                format: (y) => {
                    return NumberUtils.formatNumberToMK(y);
                }
            }
        }
    },
    tooltip: {
        contents: function (data) {
            return `<figure class="bb-tooltip bb-tooltip-date">
                        <figcaption>${formatDate(
                            data[0].x,
                            'dd-MM-yyyy',
                            'en-SE'
                        )}</figcaption>
                        <b>${NumberUtils.formatNumberToSeperated(data[0].value)}</b>
                    </figure>`;
        }
    },
    zoom: {
        enabled: true
    }
};
